import { type ReactElement } from 'react';
import { resolveExecutionRunRunSummary } from '@/features/executionRuns/components/executionRunRunSummary';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { resolveTableV2BodyRowHeight } from '@/features/shared/components/tableSystem/TableV2Heights';
import { getTableV2LinkRoutingProps } from '@/features/shared/components/tableSystem/TableV2LinkRouting';
import {
  TableV2ChevronButton,
  tableV2ChevronCellSx,
  tableV2NamePrimaryFontSize,
  TableV2ProjectCell,
  TableV2TagGroup,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import {
  resolveProjectDisplayStatus,
  resolveProjectDisplayStatusLabel,
} from '@/features/shared/utils/projectStatusPresentation';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { s, typographyTokens } from '@/theme/tokens';
import { OverflowTooltip } from '@/ui/composites/table/cells/OverflowTooltip';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { Box, Link, TableCell, TableRow, Text } from '@/ui/primitives';
import { ExpandedRunDetails } from './ExecutionRunsTableV2NestedRows';
import {
  countSuites,
  countTests,
  formatUploadedAt,
  PassRateCell,
  relativeTimeSx,
  StatusChip,
  nestedCarrierRowSx,
  nestedDetailCellSx,
} from './ExecutionRunsTableV2RowShared';
import type { TagColor } from '@/shared/data/tags';
import type { ExecutionRunDto, ProjectDto, SuiteRunDto, TestRunDto } from '@flowqa/shared';

type ExecutionRunRowProps = {
  deleteRowPendingId: string | null;
  editRowPendingId: string | null;
  expanded: boolean;
  groupErroredWithFailed: boolean;
  isLastVisibleRow: boolean;
  organisationId: string | null;
  resolveGroupErroredWithFailed?: (projectId: string) => boolean;
  project: Pick<ProjectDto, 'badgeChars' | 'badgeColor' | 'badgeEnabled' | 'name'> | null;
  row: ExecutionRunDto;
  showProjectColumn: boolean;
  showResultsFileName?: boolean;
  suiteRunDeleteRowPendingId: string | null;
  suiteRunEditRowPendingId: string | null;
  tagColorByName: Partial<Record<string, TagColor>>;
  testRunDeleteRowPendingId: string | null;
  testRunEditRowPendingId: string | null;
  onDelete: (row: ExecutionRunDto) => void;
  onDeleteSuiteRun: (row: SuiteRunDto) => void;
  onDeleteTestRun: (row: TestRunDto) => void;
  onEdit: (row: ExecutionRunDto) => void;
  onEditSuiteRun: (row: SuiteRunDto) => void;
  onEditTestRun: (row: TestRunDto) => void;
  onToggle: () => void;
};

export const ExecutionRunRow = ({
  deleteRowPendingId,
  editRowPendingId,
  expanded,
  groupErroredWithFailed,
  isLastVisibleRow,
  organisationId,
  resolveGroupErroredWithFailed,
  project,
  row,
  showProjectColumn,
  showResultsFileName = true,
  suiteRunDeleteRowPendingId,
  suiteRunEditRowPendingId,
  tagColorByName,
  testRunDeleteRowPendingId,
  testRunEditRowPendingId,
  onDelete,
  onDeleteSuiteRun,
  onDeleteTestRun,
  onEdit,
  onEditSuiteRun,
  onEditTestRun,
  onToggle,
}: ExecutionRunRowProps): ReactElement => {
  const rowGroupErroredWithFailed =
    resolveGroupErroredWithFailed?.(row.projectId) ?? groupErroredWithFailed;
  const displayStatus = resolveProjectDisplayStatus(row.status, rowGroupErroredWithFailed);
  const statusLabel = resolveProjectDisplayStatusLabel(row.status, rowGroupErroredWithFailed);
  const runSummary = resolveExecutionRunRunSummary(row, tagColorByName, { showResultsFileName });
  const suiteCounts = countSuites(row);
  const testCounts = countTests(row);
  const ranAt = row.startedAt ?? row.finishedAt;
  const ranAtLabel = formatRelativeTime(ranAt);
  const ranAtAbsoluteLabel = formatUploadedAt(ranAt);
  const uploadedAt = row.resultsFileCreatedAt ?? row.startedAt;
  const uploadedAtLabel = formatRelativeTime(uploadedAt);
  const uploadedAtAbsoluteLabel = formatUploadedAt(uploadedAt);
  const durationLabel = formatDuration(row.durationMs);
  const bodyRowHeight = resolveTableV2BodyRowHeight(!expanded && !isLastVisibleRow);

  return (
    <>
      <TableRow
        sx={{
          backgroundColor: 'background.paper',
          '& > td': {
            borderBottom:
              expanded || isLastVisibleRow ? 0 : (theme) => `1px solid ${theme.app.colors.border}`,
            boxSizing: 'border-box',
            height: bodyRowHeight,
            maxHeight: bodyRowHeight,
            overflow: 'hidden',
            py: s('md'),
            verticalAlign: 'middle',
          },
        }}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse run details' : 'Expand run details'}
            onClick={onToggle}
          />
        </TableCell>
        <TableCell>
          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
            <Box
              sx={{
                alignContent: 'center',
                display: 'grid',
                gap: s('none'),
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              <Text
                role="body"
                component="div"
                sx={{
                  '&.MuiTypography-root': {
                    fontSize: tableV2NamePrimaryFontSize,
                  },
                  fontSize: tableV2NamePrimaryFontSize,
                  fontWeight: typographyTokens.body.fontWeight,
                  lineHeight: 1.2,
                }}
              >
                <OverflowTooltip title={runSummary.primary.label} inline>
                  <Link
                    {...getTableV2LinkRoutingProps(runSummary.primary.href)}
                    sx={{
                      '&.MuiTypography-root': {
                        fontSize: tableV2NamePrimaryFontSize,
                      },
                      color: 'text.primary',
                      display: 'inline-block',
                      fontSize: tableV2NamePrimaryFontSize,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textDecorationColor: 'currentColor',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {runSummary.primary.label}
                  </Link>
                </OverflowTooltip>
              </Text>
              {runSummary.secondary ? (
                <Text
                  role="captionSmall"
                  color="textSecondary"
                  component="div"
                  sx={{
                    fontSize: (theme) => theme.typography.caption.fontSize,
                    lineHeight: 1.2,
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  <OverflowTooltip title={runSummary.secondary.label} inline>
                    <Link
                      {...getTableV2LinkRoutingProps(runSummary.secondary.href ?? '/results-files')}
                      sx={{
                        color: 'text.secondary',
                        display: 'inline-block',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textDecorationColor: 'currentColor',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {runSummary.secondary.label}
                    </Link>
                  </OverflowTooltip>
                </Text>
              ) : null}
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <StatusChip status={displayStatus} label={statusLabel} />
        </TableCell>
        <TableCell>
          <PassRateCell counts={testCounts.total > 0 ? testCounts : suiteCounts} />
        </TableCell>
        <TableCell>
          <Text role="body" color="textSecondary" component="span">
            {durationLabel.length > 0 ? durationLabel : '-'}
          </Text>
        </TableCell>
        <TableCell>
          <Box sx={{ alignContent: 'center', display: 'grid', gap: s('none'), minWidth: 0 }}>
            <OverflowTooltip title={ranAtAbsoluteLabel} wrapperSx={{ lineHeight: 1.4 }}>
              <Text
                role="body"
                color="textSecondary"
                component="span"
                sx={{
                  display: 'block',
                  lineHeight: 1.4,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {ranAtAbsoluteLabel}
              </Text>
            </OverflowTooltip>
            <Text
              role="captionSmall"
              component="span"
              sx={[
                relativeTimeSx,
                {
                  lineHeight: 1.4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              ]}
            >
              {ranAtLabel.length > 0 ? ranAtLabel : '-'}
            </Text>
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={{ alignContent: 'center', display: 'grid', gap: s('none'), minWidth: 0 }}>
            <OverflowTooltip title={uploadedAtAbsoluteLabel} wrapperSx={{ lineHeight: 1.4 }}>
              <Text
                role="body"
                color="textSecondary"
                component="span"
                sx={{
                  display: 'block',
                  lineHeight: 1.4,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {uploadedAtAbsoluteLabel}
              </Text>
            </OverflowTooltip>
            <Text
              role="captionSmall"
              component="span"
              sx={[
                relativeTimeSx,
                {
                  lineHeight: 1.4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              ]}
            >
              {uploadedAtLabel.length > 0 ? uploadedAtLabel : '-'}
            </Text>
          </Box>
        </TableCell>
        <TableCell>
          <TableV2TagGroup
            tagColorByName={Object.fromEntries(runSummary.tags.map((tag) => [tag.label, tag.tone]))}
            tags={runSummary.tags.map((tag) => tag.label)}
          />
        </TableCell>
        {showProjectColumn ? (
          <TableCell>
            <TableV2ProjectCell project={project} fallbackName={row.projectName} />
          </TableCell>
        ) : null}
        <TableCell align="center" sx={{ width: s('5xl') }}>
          <TableActionsCell
            rowName={row.name?.trim() ?? row.id}
            editActionLabel={getEntityActionLabel('edit', 'Execution Run')}
            deleteActionLabel={getEntityActionLabel('delete', 'Execution Run')}
            onEdit={() => {
              onEdit(row);
            }}
            onDelete={() => {
              onDelete(row);
            }}
            disableEdit={editRowPendingId === row.id || deleteRowPendingId === row.id}
            disableDelete={deleteRowPendingId === row.id}
          />
        </TableCell>
      </TableRow>
      {expanded ? (
        <TableRow
          sx={[
            nestedCarrierRowSx,
            !isLastVisibleRow
              ? {
                  '& > td': {
                    borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
                  },
                }
              : {},
          ]}
        >
          <TableCell colSpan={showProjectColumn ? 10 : 9} sx={nestedDetailCellSx}>
            <ExpandedRunDetails
              expanded={expanded}
              organisationId={organisationId}
              row={row}
              tagColorByName={tagColorByName}
              suiteRunDeleteRowPendingId={suiteRunDeleteRowPendingId}
              suiteRunEditRowPendingId={suiteRunEditRowPendingId}
              testRunDeleteRowPendingId={testRunDeleteRowPendingId}
              testRunEditRowPendingId={testRunEditRowPendingId}
              onDeleteSuiteRun={onDeleteSuiteRun}
              onDeleteTestRun={onDeleteTestRun}
              onEditSuiteRun={onEditSuiteRun}
              onEditTestRun={onEditTestRun}
            />
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
};
